import { DensityEnum, ThemesEnum } from '@shared/enums';
import moment from 'moment';

import { OverlayContainer } from '@angular/cdk/overlay';
import {
  Component,
  OnInit,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { FeatureFlagsService } from '../../../services';
// interface
import { NAVIGATION_ITEMS, SidenavInterface } from '../../interfaces';
import { BuildInfoService, ThemeService } from '../../services';
import { AuthService } from '../../services/auth/auth.service';

@Component({
  selector: 'phoenix-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: false,
})
export class SidenavComponent implements OnInit {
  isExpanded = signal(false);
  themes: ThemesEnum[] = Object.values(ThemesEnum);
  densities: DensityEnum[] = Object.values(DensityEnum);
  userPermissions = signal<string[] | null>(null);

  public navigationItemsSignal = computed(() => {
    return NAVIGATION_ITEMS.filter(item =>
      this.featureFlags.isOn(item.featureFlag)
    )
      .map(item => ({
        ...item,
        iconClass: item.iconClass ?? 'material-icons-outlined',
        children: item.children
          ?.filter(child => this.featureFlags.isOn(child.featureFlag))
          .map(child => ({
            ...child,
            iconClass: child.iconClass ?? 'material-icons-outlined',
          })),
      }))
      .filter(item => this.hasVisibleChildren(item));
  });

  public readonly featureFlags = inject(FeatureFlagsService);
  private readonly _overlayContainer = inject(OverlayContainer);
  readonly _themes = inject(ThemeService);
  readonly _buildInfo = inject(BuildInfoService);
  authService = inject(AuthService);
  router = inject(Router);

  private readonly _dateInputTypes = ['date', 'datetime-local', 'time'];
  private readonly _overlaySelectors =
    '.mat-mdc-autocomplete-panel, .mat-datepicker-content, .mat-mdc-datepicker-content, .mat-calendar';

  ngOnInit(): void {
    this.authService
      .getUserPermissions()
      .then(permissions => this.userPermissions.set(permissions))
      .catch(() => this.userPermissions.set([]));
  }

  mouseenter() {
    if (this.isOverlayOpen()) {
      return;
    }
    this.isExpanded.set(true);
  }

  mouseleave() {
    this.isExpanded.set(false);
  }

  private isOverlayOpen(): boolean {
    // Check if a native date/time input is focused (native picker may be open)
    const activeEl = document.activeElement as HTMLInputElement | null;
    if (activeEl?.type && this._dateInputTypes.includes(activeEl.type)) {
      return true;
    }
    // Check if an Angular Material overlay panel is open
    const container = this._overlayContainer.getContainerElement();
    return !!container?.querySelector(this._overlaySelectors);
  }

  public userImg: WritableSignal<string> = signal(
    this.authService.currentUser()?.photoURL ?? ''
  );

  public userEmail: WritableSignal<string> = signal(
    this.authService.currentUser()?.email ?? 'Guest'
  );

  public hasUnreadNotifications(): boolean {
    // Setup for more notifications in the future

    // wrapped feature notification
    if (this.featureFlags.isOn('show_yearly_wrapped')) {
      const storageKey = `wrapped-${moment.utc().year()}-notification`;
      const isWrappedNotificationHidden =
        localStorage.getItem(storageKey) === 'hidden';
      return !isWrappedNotificationHidden;
    }

    return false;
  }

  public changeTheme(theme: ThemesEnum) {
    this._themes.setTheme(theme);
  }

  public changeDensity(density: DensityEnum) {
    this._themes.setDensity(density);
  }

  public getDensity(value: DensityEnum) {
    return Object.keys(DensityEnum).find(
      key => DensityEnum[key as keyof typeof DensityEnum] === value
    );
  }

  urlContains(url: string): boolean {
    return this.router.url.includes(url);
  }

  isParentActive(item: SidenavInterface): boolean {
    if (item.children) {
      return item.children.some(subitem =>
        this.urlContains(subitem.path ?? '')
      );
    }
    return this.urlContains(item.path ?? '');
  }

  private hasVisibleChildren(item: SidenavInterface): boolean {
    if (!item.children) {
      return true;
    }

    if (item.children.length === 0) {
      return false;
    }

    return item.children.some(child => this.hasPermission(child.permissions));
  }

  private hasPermission(requiredPermissions?: string[]): boolean {
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    const userPermissions = this.userPermissions();
    if (!userPermissions) {
      return true;
    }

    if (requiredPermissions.includes('SOME')) {
      return requiredPermissions.some(permission =>
        userPermissions.includes(permission)
      );
    }

    return requiredPermissions.every(permission =>
      userPermissions.includes(permission)
    );
  }
}
